import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { logger } from '../utils/logger';
import axios from 'axios';
import { Database, ArrowClockwise, Lightning, Clock, CheckCircle, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CacheMonitor = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const buttonRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, right: 0 });

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/cache-stats`);
      setStats(response.data);
    } catch (error) {
      logger.error('Error fetching cache stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calcular posición del panel cuando se abre
  useEffect(() => {
    if (isExpanded && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPanelPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isExpanded]);

  if (!stats) return null;

  const hitRate = stats.cache_entries > 0 
    ? ((stats.valid_entries / stats.cache_entries) * 100).toFixed(0)
    : 0;

  const getStatusColor = () => {
    if (hitRate >= 80) return { bg: '#00ff6a', glow: 'rgba(0, 255, 106, 0.5)' };
    if (hitRate >= 50) return { bg: '#fbbf24', glow: 'rgba(251, 191, 36, 0.5)' };
    return { bg: '#ef4444', glow: 'rgba(239, 68, 68, 0.5)' };
  };

  const statusColor = getStatusColor();

  return (
    <div className="relative">
      {/* Botón 3D Premium */}
      <button
        ref={buttonRef}
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 group"
        style={{
          background: isExpanded 
            ? 'linear-gradient(145deg, rgba(0, 255, 106, 0.2) 0%, rgba(0, 200, 80, 0.1) 100%)'
            : 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
          border: `1px solid ${isExpanded ? 'rgba(0, 255, 106, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
          boxShadow: isExpanded 
            ? '0 4px 15px rgba(0, 255, 106, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
            : '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
        title="Monitor de Caché"
      >
        {/* Icono con efecto 3D */}
        <div 
          className="relative w-6 h-6 rounded-md flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
          style={{
            background: `linear-gradient(145deg, rgba(0, 255, 106, 0.3) 0%, rgba(0, 255, 106, 0.1) 100%)`,
            border: `1px solid rgba(0, 255, 106, 0.5)`,
            boxShadow: `0 2px 8px rgba(0, 255, 106, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)`
          }}
        >
          <Database 
            size={14} 
            weight="fill"
            style={{ 
              color: '#00ff6a',
              filter: `drop-shadow(0 0 4px rgba(0, 255, 106, 0.6))`
            }} 
          />
        </div>
        
        {/* Stats compactos */}
        <div className="flex items-center gap-1.5">
          <span 
            className="font-mono font-bold text-base"
            style={{ 
              color: '#00ff6a',
              textShadow: `0 0 12px rgba(0, 255, 106, 0.6), 0 0 20px rgba(0, 255, 106, 0.3)`
            }}
          >
            {hitRate}%
          </span>
          <span className="text-gray-500 text-xs">|</span>
          <span className="font-mono text-gray-400 text-xs">{stats.cache_entries}</span>
        </div>

        {/* Indicador de estado */}
        <div 
          className="w-2 h-2 rounded-full animate-pulse"
          style={{ 
            background: '#00ff6a',
            boxShadow: `0 0 8px rgba(0, 255, 106, 0.6)`
          }}
        />
      </button>

      {/* Panel expandido 3D - usando Portal para estar sobre todo */}
      {isExpanded && createPortal(
        <>
          {/* Overlay para cerrar */}
          <div 
            className="fixed inset-0"
            style={{ zIndex: 99998 }}
            onClick={() => setIsExpanded(false)}
          />
          
          <div 
            className="fixed w-80 rounded-2xl overflow-hidden"
            style={{
              top: panelPosition.top,
              right: panelPosition.right,
              zIndex: 99999,
              background: 'linear-gradient(165deg, rgba(12, 25, 48, 0.98) 0%, rgba(8, 16, 32, 1) 50%, rgba(5, 12, 25, 1) 100%)',
              border: '1px solid rgba(0, 255, 106, 0.3)',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9), 0 0 40px rgba(0, 255, 106, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)',
              backdropFilter: 'blur(20px)',
            }}
          >
            {/* Línea neón superior */}
            <div 
              className="h-[3px]"
              style={{
                background: `linear-gradient(90deg, transparent 0%, #00ff6a 20%, #00ff6a 50%, #00ff6a 80%, transparent 100%)`,
                boxShadow: `0 0 20px rgba(0, 255, 106, 0.6)`
              }}
            />

            {/* Header */}
            <div 
              className="px-5 py-4 flex items-center justify-between"
              style={{ 
                borderBottom: '1px solid rgba(0, 255, 106, 0.15)',
                background: 'linear-gradient(180deg, rgba(0, 255, 106, 0.05) 0%, transparent 100%)'
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(145deg, rgba(0, 255, 106, 0.25) 0%, rgba(0, 200, 80, 0.1) 100%)',
                    border: '1px solid rgba(0, 255, 106, 0.4)',
                    boxShadow: '0 4px 15px rgba(0, 255, 106, 0.25), inset 0 1px 0 rgba(255,255,255,0.1)'
                  }}
                >
                  <Database size={20} weight="fill" className="text-green-400" style={{ filter: 'drop-shadow(0 0 6px rgba(0, 255, 106, 0.6))' }} />
                </div>
                <div>
                  <h3 
                    className="text-base font-bold"
                    style={{
                      background: 'linear-gradient(180deg, #ffffff 0%, #00ff6a 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Monitor de Caché
                  </h3>
                  <span className="text-xs text-gray-500">Sistema de almacenamiento</span>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); fetchStats(); }}
                disabled={loading}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-110"
                style={{
                  background: 'linear-gradient(145deg, rgba(0, 255, 106, 0.15) 0%, rgba(0, 200, 80, 0.05) 100%)',
                  border: '1px solid rgba(0, 255, 106, 0.3)',
                }}
              >
                <ArrowClockwise 
                  size={16} 
                  className={`text-green-400 ${loading ? 'animate-spin' : ''}`} 
                />
              </button>
            </div>

            {/* Contenido */}
            <div className="p-5 space-y-4">
              {/* Barra de eficiencia 3D */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-400 font-medium">Eficiencia del Caché</span>
                  <span 
                    className="text-lg font-bold font-mono"
                    style={{ 
                      color: statusColor.bg,
                      textShadow: `0 0 15px ${statusColor.glow}`
                    }}
                  >
                    {hitRate}%
                  </span>
                </div>
                <div 
                  className="h-3 rounded-full overflow-hidden"
                  style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <div 
                    className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{ 
                      width: `${hitRate}%`,
                      background: `linear-gradient(90deg, ${statusColor.bg}80 0%, ${statusColor.bg} 100%)`,
                      boxShadow: `0 0 15px ${statusColor.glow}, inset 0 1px 0 rgba(255,255,255,0.3)`
                    }}
                  />
                </div>
              </div>

              {/* Stats Grid 3D */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Total', value: stats.cache_entries, icon: Database, color: '#00dcff' },
                  { label: 'Válidos', value: stats.valid_entries, icon: CheckCircle, color: '#00ff6a' },
                  { label: 'Expirados', value: stats.expired_entries, icon: Clock, color: '#fbbf24' },
                  { label: 'TTL', value: `${stats.ttl_seconds}s`, icon: Lightning, color: '#a855f7' },
                ].map((item) => (
                  <div 
                    key={`stat-${item.label}`}
                    className="p-3 rounded-xl transition-all duration-200 hover:scale-[1.02]"
                    style={{
                      background: 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.2) 100%)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)'
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <item.icon size={14} style={{ color: item.color }} />
                      <span className="text-xs text-gray-500">{item.label}</span>
                    </div>
                    <div 
                      className="text-lg font-bold font-mono"
                      style={{ color: item.color }}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tipos de caché */}
              {stats.cache_types && Object.keys(stats.cache_types).length > 0 && (
                <div 
                  className="p-3 rounded-xl"
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(0, 255, 106, 0.1)'
                  }}
                >
                  <div className="text-xs text-gray-500 mb-2 font-medium">Por tipo de datos:</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.cache_types).map(([type, count]) => (
                      <span 
                        key={type}
                        className="px-2.5 py-1 rounded-lg text-xs font-mono font-medium"
                        style={{
                          background: 'linear-gradient(145deg, rgba(0, 255, 106, 0.15) 0%, rgba(0, 200, 80, 0.05) 100%)',
                          border: '1px solid rgba(0, 255, 106, 0.2)',
                          color: '#00ff6a'
                        }}
                      >
                        {type}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Rate limit footer */}
              <div 
                className="flex items-center justify-between pt-3 text-xs"
                style={{ borderTop: '1px solid rgba(0, 255, 106, 0.1)' }}
              >
                <span className="text-gray-500">Rate limit delay</span>
                <span className="font-mono text-green-400">{stats.rate_limit_delay_ms}ms</span>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default CacheMonitor;
